import React from 'react';
import {
  Loader2, AlertTriangle, Clock, ChevronRight,
  Bell, X, MonitorSmartphone, Share, MoreVertical, SmartphoneNfc, MapPin, MessageCircle,
  Home, ClipboardList, UserCircle, Check
} from 'lucide-react';
import { AppProvider, useAppContext } from './context/AppContext';
import { getWorkerReviewStatus, getWorkerReviewTone, PLATFORM_WA, openWhatsApp } from './constants';

import AuthFlow from './views/AuthFlow';
import HomeView from './views/HomeView';
import OrderView from './views/OrderView';
import PaymentView from './views/PaymentView';
import ProfileView from './views/ProfileView';
import EditProfileView from './views/EditProfileView';
import WalletView from './views/WalletView';
import HistoryView from './views/HistoryView';
import SuggestionView from './views/SuggestionView';
import LegalView from './views/LegalView';
import AdminView from './views/AdminView';

function AppShell() {
  const {
    lang, setLang, t, user, userData, role, loading,
    view, setView, authMode,
    toast, setToast, previewImage, setPreviewImage,
    showSplash, fadeSplash, showInstallPrompt, installDevice,
    yappySimulating, yappySuccessData, setYappySuccessData,
    handleBannerClick, dismissInstallPrompt, requireAuthView,
  } = useAppContext();

  if (loading) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader2 className="animate-spin" color="#2962FF" size={40} />
      </div>
    );
  }

  const activeAuthMode = authMode || (user && !role ? 'role' : null);
  if (activeAuthMode) return <AuthFlow />;

  // Worker readiness banner computed values
  const workerHasDocs = Boolean(userData?.idPhoto && userData?.selfie);
  const workerHasSkills = Boolean(userData?.skills && userData.skills.length > 0);
  const workerHasBank = Boolean(userData?.bankInfo?.accName && userData?.bankInfo?.accNum && userData?.bankInfo?.accType);
  const workerHasInvoiceTerms = Boolean(userData?.invoice_terms_accepted);
  const workerCanResubmit = Boolean(
    workerHasDocs && workerHasSkills && workerHasBank && workerHasInvoiceTerms &&
    userData?.firstName && userData?.lastName && userData?.phone && userData?.address
  );
  const workerCanReceiveOrders = role !== 'worker' || (userData?.isVerified && workerHasSkills);
  const workerReviewStatus = role === 'worker' ? getWorkerReviewStatus(userData || {}) : null;
  const workerReviewTone = workerReviewStatus ? getWorkerReviewTone(workerReviewStatus) : { bg: '#FFF7ED', color: '#92400E', border: '#FCD34D' };

  const profileViews = new Set(['profile', 'wallet', 'editProfile', 'legal', 'admin']);

  return (
    <div style={{ maxWidth: '480px', margin: '0 auto', minHeight: '100vh', backgroundColor: '#F8FAFC', paddingBottom: '85px', position: 'relative' }}>

      {/* Yappy Success Overlay */}
      {yappySuccessData && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999998, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: '#fff', borderRadius: '20px', padding: '30px 24px', width: '100%', maxWidth: '380px', textAlign: 'center' }}>
            <div style={{ width: '70px', height: '70px', background: '#ECFDF3', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <Check size={36} color="#16A34A" />
            </div>
            <h2 style={{ margin: '0 0 8px', fontSize: '22px', fontWeight: '900', color: '#111' }}>
              {lang === 'es' ? '¡Pago confirmado!' : '支付成功！'}
            </h2>
            <p style={{ margin: '0 0 20px', fontSize: '14px', color: '#555', lineHeight: '1.5' }}>
              {lang === 'es'
                ? `Tu pedido fue creado. Ref: ${yappySuccessData.orderId}`
                : `订单已创建。单号：${yappySuccessData.orderId}`}
            </p>
            <button onClick={() => { setYappySuccessData(null); setView('history'); }}
              style={{ width: '100%', padding: '16px', backgroundColor: '#16A34A', color: '#fff', borderRadius: '12px', border: 'none', fontWeight: '900', fontSize: '16px', cursor: 'pointer' }}>
              {lang === 'es' ? '✓ Ver mi pedido' : '✓ 查看我的订单'}
            </button>
          </div>
        </div>
      )}

      {/* Yappy Simulating Overlay */}
      {yappySimulating && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: '#004481', zIndex: 9999999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
          <div style={{ width: '80px', height: '80px', backgroundColor: '#fff', borderRadius: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
            <SmartphoneNfc size={40} color="#004481" />
          </div>
          <h2 style={{ margin: '0 0 10px 0' }}>{t.yappy_sim_title}</h2>
          <p style={{ fontSize: '12px', opacity: 0.8 }}>{t.yappy_sim_desc}</p>
          <Loader2 className="animate-spin" size={30} color="#fff" style={{ marginTop: '30px' }} />
        </div>
      )}

      {/* Splash Screen */}
      {showSplash && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: '#111', zIndex: 999999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#fff', transition: 'opacity 0.5s ease', opacity: fadeSplash ? 0 : 1 }}>
          <div style={{ marginBottom: '20px' }}>
            <span style={{ color: '#D4AF37', fontSize: '42px', fontWeight: '900', letterSpacing: '6px', fontFamily: 'Georgia, serif', textShadow: '0 4px 20px rgba(212,175,55,0.3)' }}>FIXBOX</span>
          </div>
          <p style={{ fontSize: '12px', color: '#999', fontWeight: '500', letterSpacing: '2px', textTransform: 'uppercase' }}>PLATFORM DE REPARACIONES</p>
          <Loader2 className="animate-spin" size={24} color="#D4AF37" style={{ marginTop: '40px', opacity: 0.8 }} />
        </div>
      )}

      {/* Install PWA Banner */}
      {showInstallPrompt && (
        <div onClick={handleBannerClick} style={{ position: 'fixed', bottom: '95px', left: '15px', right: '15px', backgroundColor: '#fff', padding: '15px', borderRadius: '20px', boxShadow: '0 10px 40px rgba(0,122,255,0.2)', zIndex: 9999, display: 'flex', gap: '15px', alignItems: 'center', border: '2px solid #007AFF', animation: 'slideUp 0.5s ease', cursor: 'pointer' }}>
          <div style={{ backgroundColor: '#F0F9FF', padding: '12px', borderRadius: '15px' }}>
            <MonitorSmartphone color="#007AFF" size={28} />
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ margin: '0 0 5px 0', fontSize: '15px', fontWeight: '900', color: '#333' }}>Instalar FixBox 🚀</p>
            {installDevice === 'ios'
              ? <p style={{ margin: 0, fontSize: '12px', color: '#666', lineHeight: '1.4' }}>Toca el ícono <Share size={14} color="#007AFF" style={{ verticalAlign: 'middle' }} /> abajo y luego <b>"Agregar a inicio"</b>.</p>
              : <p style={{ margin: 0, fontSize: '12px', color: '#666', lineHeight: '1.4' }}>Toca <MoreVertical size={14} color="#007AFF" style={{ verticalAlign: 'middle' }} /> arriba y selecciona <b>"Instalar aplicación"</b>.</p>}
          </div>
          <X size={24} color="#ccc" onClick={dismissInstallPrompt} style={{ padding: '5px' }} />
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div style={{ position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#1C1C1E', color: '#fff', padding: '15px 20px', borderRadius: '15px', zIndex: 99999, display: 'flex', alignItems: 'center', gap: '15px', boxShadow: '0 10px 30px rgba(0,0,0,0.3)', width: '90%', maxWidth: '400px', transition: 'all 0.3s ease' }}>
          <div style={{ backgroundColor: '#007AFF', borderRadius: '50%', padding: '8px' }}>
            <Bell size={20} color="#fff" />
          </div>
          <div style={{ flex: 1 }}>
            <b style={{ display: 'block', fontSize: '14px', marginBottom: '2px' }}>{toast.title}</b>
            <span style={{ fontSize: '12px', color: '#E5E7EB', lineHeight: '1.4' }}>{toast.desc}</span>
          </div>
          <X size={18} color="#9CA3AF" onClick={() => setToast(null)} style={{ cursor: 'pointer' }} />
        </div>
      )}

      {/* Worker Readiness Warning Banner */}
      {role === 'worker' && !workerCanReceiveOrders && view !== 'editProfile' && (
        <div onClick={() => setView('editProfile')} style={{ backgroundColor: workerReviewTone.bg, color: workerReviewTone.color, padding: '12px 15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', fontWeight: '800', fontSize: '13px', borderBottom: `1px solid ${workerReviewTone.border}` }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px', lineHeight: '1.35' }}>
            {workerReviewStatus === 'PENDING' ? <Clock size={16} /> : <AlertTriangle size={16} />}
            {workerReviewStatus === 'REJECTED'
              ? (workerCanResubmit
                ? (lang === 'es' ? 'Perfil corregido: toca reenviar a revisión.' : '资料已补齐：请点击重新提交审核。')
                : (lang === 'es' ? `Perfil rechazado: ${userData?.verification_rejection || 'revisa tus datos'}` : `资料被驳回：${userData?.verification_rejection || '请检查资料'}`))
              : workerReviewStatus === 'PENDING'
                ? (lang === 'es' ? 'Perfil en revisión. Te avisaremos cuando puedas tomar pedidos.' : '资料审核中，通过后即可接单。')
                : (!workerHasDocs
                  ? (lang === 'es' ? 'Sube cédula y selfie para activar tu cuenta.' : '请上传证件和自拍，才能进入审核。')
                  : !workerHasSkills
                    ? (lang === 'es' ? 'Elige tus servicios para recibir pedidos.' : '请选择可接服务类型，才能收到订单。')
                    : !workerHasInvoiceTerms
                      ? (lang === 'es' ? 'Confirma la regla de factura para técnicos.' : '请确认师傅发票责任规则。')
                      : (lang === 'es' ? 'Completa tus datos de cobro.' : '请完善收款账户。'))}
          </span>
          <ChevronRight size={16} />
        </div>
      )}

      {/* Location Header */}
      <div style={{ backgroundColor: '#fff', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #E8ECF0', boxShadow: '0 1px 6px rgba(0,0,0,0.04)' }}>
        {/* 左：城市定位 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
          <MapPin size={16} color="#2962FF" />
          <b style={{ color: '#17202A', fontSize: '15px', fontWeight: '800' }}>Ciudad de Panamá</b>
          <span style={{ color: '#9CA3AF', fontSize: '13px' }}>▾</span>
        </div>
        {/* 右：消息图标 + 语言切换 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button onClick={() => setLang(lang === 'es' ? 'zh' : 'es')} style={{ border: 'none', background: '#F5F7FA', padding: '5px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '800', color: '#374151', cursor: 'pointer' }}>
            {lang === 'es' ? '中文' : 'ES'}
          </button>
          <div style={{ position: 'relative', cursor: 'pointer' }}
            onClick={() => openWhatsApp(PLATFORM_WA, lang === 'es' ? 'Hola FixBox, necesito soporte...' : '你好 FixBox，我需要客服帮助...')}>
            <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: '#F5F7FA', border: '1px solid #E8ECF0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <MessageCircle size={20} color="#374151" />
            </div>
            <div style={{ position: 'absolute', top: '1px', right: '1px', width: '10px', height: '10px', borderRadius: '50%', background: '#FF4D4F', border: '2px solid #fff' }} />
          </div>
        </div>
      </div>

      {/* Main View Router */}
      <div>
        {view === 'home' && <HomeView />}
        {view === 'order' && <OrderView />}
        {view === 'payment' && <PaymentView />}
        {view === 'profile' && <ProfileView />}
        {view === 'editProfile' && <EditProfileView />}
        {view === 'wallet' && <WalletView />}
        {view === 'history' && <HistoryView />}
        {view === 'suggestion' && <SuggestionView />}
        {view === 'legal' && <LegalView />}
        {view === 'admin' && <AdminView />}
      </div>

      {/* Image Preview Overlay */}
      {previewImage && (
        <div onClick={() => setPreviewImage(null)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.95)', zIndex: 999999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <img src={previewImage} onClick={e => e.stopPropagation()} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: '10px' }} alt="fullscreen" />
          <X color="#fff" size={40} onClick={() => setPreviewImage(null)} style={{ position: 'absolute', top: 40, right: 20, cursor: 'pointer', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '50%', padding: '5px' }} />
        </div>
      )}

      {/* Bottom Navigation */}
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, height: '85px', backgroundColor: '#fff', display: 'flex', justifyContent: 'space-around', alignItems: 'center', borderTop: '1px solid #E6ECF3', paddingBottom: 'env(safe-area-inset-bottom)', zIndex: 10, boxShadow: '0 -4px 20px rgba(0,0,0,0.06)' }}>
        <div onClick={() => setView('home')} style={{ textAlign: 'center', color: view === 'home' ? '#2962FF' : '#6B7280', cursor: 'pointer', flex: 1 }}>
          <Home size={22} style={{ margin: '0 auto' }} />
          <p style={{ fontSize: '10px', marginTop: '4px', fontWeight: view === 'home' ? '800' : '600' }}>{t.home}</p>
        </div>
        <div onClick={() => requireAuthView('history')} style={{ textAlign: 'center', color: view === 'history' ? '#2962FF' : '#6B7280', cursor: 'pointer', flex: 1 }}>
          <ClipboardList size={22} style={{ margin: '0 auto' }} />
          <p style={{ fontSize: '10px', marginTop: '4px', fontWeight: view === 'history' ? '800' : '600' }}>{t.orders}</p>
        </div>
        <div onClick={() => openWhatsApp(PLATFORM_WA, lang === 'es' ? 'Hola FixBox, necesito soporte...' : '你好 FixBox，我需要客服帮助...')} style={{ textAlign: 'center', color: '#25D366', cursor: 'pointer', flex: 1 }}>
          <MessageCircle size={22} style={{ margin: '0 auto' }} />
          <p style={{ fontSize: '10px', marginTop: '4px', color: '#6B7280', fontWeight: '600' }}>{t.support_btn}</p>
        </div>
        <div onClick={() => requireAuthView('profile')} style={{ textAlign: 'center', color: profileViews.has(view) ? '#2962FF' : '#6B7280', cursor: 'pointer', flex: 1 }}>
          <UserCircle size={22} style={{ margin: '0 auto' }} />
          <p style={{ fontSize: '10px', marginTop: '4px', fontWeight: profileViews.has(view) ? '800' : '600' }}>{t.profile}</p>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <AppProvider>
      <AppShell />
    </AppProvider>
  );
}
